
import React, { useEffect, useState, memo, useCallback } from "react";
import axios from "axios";
import { getToken } from "../../utils/auth";
import { useTranslation } from "react-i18next";
import CONFIG from "../../config";

const AdminSecrets = memo(() => {
    const { t, i18n } = useTranslation();
    const [secrets, setSecrets] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editingSection, setEditingSection] = useState(null);
    const [newSecret, setNewSecret] = useState("");
    const [message, setMessage] = useState("");

    const isArabic = i18n.language?.startsWith('ar');
    const [permissions, setPermissions] = useState([]);
    const [isAdmin, setIsAdmin] = useState(false);

    useEffect(() => {
        const user = JSON.parse(localStorage.getItem("user") || "{}");
        setIsAdmin(!!user.isAdmin);
        setPermissions(user.permissions || []);
    }, []);

    const hasPerm = (p) => {
        if (!isAdmin) return false;
        if (permissions.includes('*') || permissions === '*') return true;
        return permissions.includes(p);
    };

    const apiBase = `${CONFIG.API_URL}/api/admin/secrets`;

    const fetchSecrets = useCallback(async () => {
        setLoading(true);
        try {
            const token = getToken();
            const res = await axios.get(apiBase, { headers: { Authorization: `Bearer ${token}` } });
            setSecrets(res.data || []);
        } catch (err) {
            console.error(err);
            setMessage(err.response?.data?.message || t('admin.admin_secrets_page.load_failed', 'Failed to load secrets'));
        } finally {
            setLoading(false);
        }
    }, [apiBase, t]);

    useEffect(() => { fetchSecrets(); }, [fetchSecrets]);

    const handleEdit = (section, currentKey) => {
        setEditingSection(section);
        setNewSecret(currentKey);
    };

    const handleCancel = () => {
        setEditingSection(null);
        setNewSecret("");
    };

    const handleSave = async (section) => {
        try {
            const token = getToken();
            await axios.put(`${apiBase}/${section}`, { secret_key: newSecret }, { headers: { Authorization: `Bearer ${token}` } });
            setMessage(t('admin.admin_secrets_page.update_success', 'Secret updated successfully'));
            setEditingSection(null);
            fetchSecrets();
            setTimeout(() => setMessage(''), 3000);
        } catch (err) {
            console.error(err);
            setMessage(err.response?.data?.message || t('admin.admin_secrets_page.update_failed', 'Failed to update secret'));
        }
    };

    const generateRandom = () => {
        const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*";
        let result = "";
        for (let i = 0; i < 16; i++) {
            result += chars.charAt(Math.floor(Math.random() * chars.length));
        }
        setNewSecret(result);
    };

    return (
        <div className="max-w-7xl mx-auto my-12 p-8 bg-white rounded-[40px] shadow-2xl border border-gray-100 mt-24">
            <div className="flex items-center justify-between mb-12">
                <div>
                    <h2 className="text-4xl font-black text-gray-900 tracking-tight">{t("admin.admin_secrets_page.title", "Admin Secrets")}</h2>
                    <p className="text-gray-500 mt-2">{t("admin.admin_secrets_page.subtitle", "Manage access keys for different admin sections.")}</p>
                </div>
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => window.history.back()}
                        className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-6 py-3 rounded-2xl font-black text-xs uppercase tracking-widest transition-all"
                    >
                        <span className={`inline-block transition-transform group-hover:-translate-x-1 ${isArabic ? "rotate-180 group-hover:translate-x-1" : ""}`}>←</span>
                        {t('admin.common.back', 'Back')}
                    </button>
                </div>
            </div>

            {message && <div className={`mb-6 p-4 rounded-2xl font-medium border flex items-center gap-3 ${message.includes('success') ? 'bg-green-50 text-green-800 border-green-100' : 'bg-red-50 text-red-800 border-red-100'}`}>
                <i className={`fas ${message.includes('success') ? 'fa-check-circle' : 'fa-exclamation-circle'}`}></i>{message}
            </div>}

            {
                loading ? <div className="p-12 text-center text-gray-500 font-medium animate-pulse">{t('admin.common.loading')}</div> : (
                    <div className="overflow-x-auto rounded-[30px] border border-gray-100 shadow-sm">
                        <table className="w-full text-left">
                            <thead className="bg-gray-50 border-b border-gray-100 text-gray-500">
                                <tr>
                                    <th className="p-5 font-black uppercase text-[10px] tracking-widest">{t("admin.admin_secrets_page.section", "Section")}</th>
                                    <th className="p-5 font-black uppercase text-[10px] tracking-widest">{t("admin.admin_secrets_page.description", "Description")}</th>
                                    {hasPerm('secrets.value') && <th className="p-5 font-black uppercase text-[10px] tracking-widest">{t("admin.admin_secrets_page.secret_key", "Secret Key")}</th>}
                                    <th className="p-5 font-black uppercase text-[10px] tracking-widest text-right">{t('admin.common.actions', 'Actions')}</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {secrets.map(s => (
                                    <tr key={s.section} className="hover:bg-gray-50/80 transition-colors group">
                                        <td className="p-5 font-bold text-gray-900 capitalize">{s.section}</td>
                                        <td className="p-5 text-sm text-gray-600">{s.description}</td>
                                        {hasPerm('secrets.value') && (
                                            <td className="p-5 font-mono text-sm text-gray-700">
                                                {editingSection === s.section ? (
                                                    <div className="flex gap-2">
                                                        <input
                                                            value={newSecret}
                                                            onChange={e => setNewSecret(e.target.value)}
                                                            className="p-2 border rounded-lg text-xs w-64"
                                                        />
                                                        <button onClick={generateRandom} className="text-xs bg-gray-200 px-2 rounded hover:bg-gray-300" title={t("admin.admin_secrets_page.generate_random", "Generate Random")}><i className="fas fa-random"></i></button>
                                                    </div>
                                                ) : (
                                                    <span className="blur-sm hover:blur-none transition-all cursor-pointer select-all">{s.secret_key}</span>
                                                )}
                                            </td>
                                        )}
                                        <td className="p-5 text-right">
                                            <div className="flex gap-2 justify-end">
                                                {editingSection === s.section ? (
                                                    <>
                                                        <button onClick={() => handleSave(s.section)} className="bg-green-500 text-white px-3 py-1 rounded-lg text-xs font-bold hover:bg-green-600">{t("admin.common.save", "Save")}</button>
                                                        <button onClick={handleCancel} className="bg-gray-300 text-gray-700 px-3 py-1 rounded-lg text-xs font-bold hover:bg-gray-400">{t("admin.common.cancel", "Cancel")}</button>
                                                    </>
                                                ) : (
                                                    hasPerm('secrets.value') && (
                                                        <button onClick={() => handleEdit(s.section, s.secret_key)} className="bg-brand-primary/10 text-brand-primary px-3 py-1 rounded-lg text-xs font-bold hover:bg-brand-primary hover:text-white transition-all">
                                                            {t("admin.common.edit", "Edit")}
                                                        </button>
                                                    )
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )
            }
        </div >
    );
});

export default AdminSecrets;
